import { useEffect, useMemo } from 'react';
import { SIGN_IN_VIEW, TWO_FACTOR_MODE, type TwoFactorMode } from 'common/constants/auth';
import { areSignInAuthRequirementsPending } from 'pages/Auth/SignIn/SignIn.utils';
import {
  hasRememberedTwoFactor,
  isEnterpriseSsoSignInSession,
  isOtpVerified,
  resolvePostAuthView,
  toBooleanFlag,
} from 'pages/Auth/utils';
import type { UseResolvePostAuthViewParams } from './useResolvePostAuthView.types';

const useResolvePostAuthView = ({
  authRequirements,
  currentUser,
  navigate,
  resolvedProduct,
  safeReturnTo,
  updateRouteState,
  view,
}: UseResolvePostAuthViewParams) => {
  const postAuthResolution = useMemo(() => {
    if (
      !currentUser
      || areSignInAuthRequirementsPending({
        firmListStatus: authRequirements.firmListStatus,
        hasCurrentUser: true,
        isLoading: authRequirements.isLoading,
      })
    ) {
      return {
        nextView: null,
        userHasTwoFactor: false,
      };
    }

    const hasRememberedOtp = hasRememberedTwoFactor(currentUser.id);
    const bypassTwoFactorForEnterpriseSso = isEnterpriseSsoSignInSession();
    const userHasTwoFactor = authRequirements.userHasTwoFactor || toBooleanFlag(currentUser.has_2fa);

    return {
      nextView: resolvePostAuthView({
        firmListCount: authRequirements.firmList.length,
        isOtpVerified: isOtpVerified() || hasRememberedOtp,
        product: resolvedProduct,
        requiresTerms: !currentUser.profile?.terms_last_agreed,
        // Password sign-in always uses app 2FA; only enterprise SSO sessions bypass it because the IdP owns that auth step.
        requiresTwoFactor: !bypassTwoFactorForEnterpriseSso,
        user: currentUser,
        userHasTwoFactor: !bypassTwoFactorForEnterpriseSso && userHasTwoFactor,
      }),
      userHasTwoFactor,
    };
  }, [
    authRequirements.firmList.length,
    authRequirements.firmListStatus,
    authRequirements.isLoading,
    authRequirements.userHasTwoFactor,
    currentUser,
    resolvedProduct,
  ]);

  useEffect(() => {
    const { nextView, userHasTwoFactor } = postAuthResolution;

    if (!currentUser || !nextView) {
      return;
    }

    // Deep links are honored only after mandatory auth gates such as 2FA and terms are cleared.
    if (safeReturnTo && nextView !== SIGN_IN_VIEW.TWO_FACTOR && nextView !== SIGN_IN_VIEW.TERMS) {
      navigate(safeReturnTo, { replace: true });
      return;
    }

    if (nextView === SIGN_IN_VIEW.WORKSPACE_SELECTION) {
      if (view !== SIGN_IN_VIEW.WORKSPACE_SELECTION) {
        updateRouteState({
          mode: null,
          product: resolvedProduct,
          view: SIGN_IN_VIEW.WORKSPACE_SELECTION,
        });
      }

      return;
    }

    if (!nextView || nextView === view) {
      return;
    }

    let nextMode: TwoFactorMode | null = null;
    if (nextView === SIGN_IN_VIEW.TWO_FACTOR) {
      // Existing devices verify a code; users without a device enter the setup variant.
      nextMode = userHasTwoFactor ? TWO_FACTOR_MODE.VERIFY : TWO_FACTOR_MODE.SETUP;
    }

    // Product selection is the only post-auth step that should clear a previously resolved product choice.
    const nextProduct = nextView === SIGN_IN_VIEW.PRODUCT_SELECTION ? null : resolvedProduct;

    updateRouteState({
      mode: nextMode,
      product: nextProduct,
      view: nextView,
    });
  }, [
    currentUser,
    navigate,
    postAuthResolution,
    resolvedProduct,
    safeReturnTo,
    updateRouteState,
    view,
  ]);

  return postAuthResolution.nextView;
};

export default useResolvePostAuthView;
